import React, { Fragment, ReactNode, useEffect, useState } from 'react';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import {
  SalvBanner,
  SalvBannerAccent,
  SalvBannerType,
  SalvButton,
  SalvCheckbox,
  SalvCheckboxGroup,
  SalvDropdown,
  SalvGroupedCheckbox,
  SalvModal,
  SalvTextField,
  SalvTooltipWrapper,
  Size,
} from '@salvapp/ui-components';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import * as Yup from 'yup';
import { ErrorResponse } from '../common/errors/helpers';
import {
  InternalApiEntitySchema,
  InternalApiEntitySchemaField,
  InternalApiEntitySchemaFieldFeatureTypeEnum,
  InternalApiEntitySchemaFieldType,
  InternalApiEntityType,
} from '../api-internal';
import { useUpdateEntitySchema } from './entitySchemaActions';
import { v4 as uuidv4 } from 'uuid';
import { SchemaEnumOptions } from './SchemaEnumOptions';
import { SchemaChildFields } from './SchemaChildFields';
import { useCheckIfPersonsExist } from '../aml/personActions';
import { useCheckIfTransactionsExist } from '../aml/transactionActions';
import { useMe } from '../organisation/useMe';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../organisation/feature/helper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { useDispatch } from 'react-redux';
import { showConfirmationModal } from '../confirmation-modal/confirmationModalReducer';
import {
  getParentSchemaField,
  getSearchableFieldsByModification,
  isChildField,
  useIndexAndSchemaAreOutOfSyncNotification,
} from './helper';
import { ApiMappings } from '../api';
import InternalLink from '../common/InternalLink';
import { getScenarioPath } from '../navigation/helper';

const inputTypeOptions: DropdownOption[] = [
  {
    key: InternalApiEntitySchemaFieldType.TEXT,
    label: InternalApiEntitySchemaFieldType.TEXT,
    value: InternalApiEntitySchemaFieldType.TEXT,
  },
  {
    key: InternalApiEntitySchemaFieldType.KEYWORD,
    label: InternalApiEntitySchemaFieldType.KEYWORD,
    value: InternalApiEntitySchemaFieldType.KEYWORD,
  },
  {
    key: InternalApiEntitySchemaFieldType.DATETIME,
    label: InternalApiEntitySchemaFieldType.DATETIME,
    value: InternalApiEntitySchemaFieldType.DATETIME,
  },
  {
    key: InternalApiEntitySchemaFieldType.NUMBER,
    label: InternalApiEntitySchemaFieldType.NUMBER,
    value: InternalApiEntitySchemaFieldType.NUMBER,
  },
  {
    key: InternalApiEntitySchemaFieldType.OBJECT,
    label: InternalApiEntitySchemaFieldType.OBJECT,
    value: InternalApiEntitySchemaFieldType.OBJECT,
  },
  {
    key: InternalApiEntitySchemaFieldType.ENUM,
    label: InternalApiEntitySchemaFieldType.ENUM,
    value: InternalApiEntitySchemaFieldType.ENUM,
  },
];

const validationSchema = Yup.object()
  .shape({
    id: Yup.string(),
    displayName: Yup.string().required(),
    fieldName: Yup.string()
      .matches(/^\w+$/i, {
        message: 'Field name must contain letters, numbers or underscores',
      })
      .required(''),
    isMulti: Yup.boolean().required(),
    isNullable: Yup.boolean().required(),
    hidden: Yup.boolean().required(),
    searchable: Yup.boolean().required(),
    hiddenInSearchResults: Yup.boolean().required(),
    type: Yup.string().required(),
    childFields: Yup.array().of(
      Yup.object().shape({
        displayName: Yup.string().required(),
        fieldName: Yup.string()
          .matches(/^\w+$/i, {
            message: 'Field name must contain letters, numbers or underscores',
          })
          .required(''),
        type: Yup.string().required(),
        enumOptions: Yup.array().when('type', {
          is: InternalApiEntitySchemaFieldType.ENUM,
          then: (schema) => schema.of(Yup.string().required('')),
        }),
      })
    ),
    enumOptions: Yup.array().of(Yup.string()),
  })
  .test({
    test: (values) => {
      const enumOptions = values.enumOptions?.filter((option: string | undefined) => option);
      if (enumOptions && new Set(enumOptions).size !== enumOptions.length) {
        return new Yup.ValidationError(
          'The following options are not unique: ' +
            [
              ...new Set(
                enumOptions.filter(
                  (duplicate: string | undefined) =>
                    enumOptions.filter((option: string | undefined) => option === duplicate).length > 1
                )
              ),
            ].join(', '),
          null,
          'enumOptions'
        );
      } else if (enumOptions?.length !== values.enumOptions?.length) {
        return new Yup.ValidationError('', null, 'enumOptions');
      }

      return true;
    },
  })
  .test({
    test: (values) => {
      if (values.type === InternalApiEntitySchemaFieldType.OBJECT && !values.childFields?.length) {
        return new Yup.ValidationError('At least one child field is required', null, 'childFields');
      }
      return true;
    },
  });

interface Props {
  schemaField: InternalApiEntitySchemaField | undefined;
  schema: InternalApiEntitySchema;
  attributeMappings: ApiMappings;
  onClose: () => void;
  setModalClass: (className: string) => void;
}

export type FormikValues = InternalApiEntitySchemaField & {
  childFields: InternalApiEntitySchemaField[];
};

const EntitySchemaField = ({ schemaField, schema, attributeMappings, onClose: propsOnClose, setModalClass }: Props) => {
  const { features } = useMe();
  const dispatch = useDispatch();
  const [bannerMessages, setBannerMessages] = useState('');

  const onClose = () => {
    propsOnClose();
    setBannerMessages('');
  };

  const maxParentSortOrder = schema.schemaFields
    .filter((schemaField) => !schemaField.parentId)
    .reduce((max, field) => (field.sortOrder > max ? field.sortOrder : max), 0);
  const initialValues: FormikValues = {
    id: schemaField?.id || uuidv4(),
    displayName: schemaField?.displayName || '',
    fieldName: schemaField?.fieldName || '',
    isMulti: schemaField?.isMulti !== undefined ? schemaField.isMulti : false,
    type: schemaField?.type || InternalApiEntitySchemaFieldType.TEXT,
    hidden: schemaField ? schemaField.hidden : false,
    searchable: schemaField ? schemaField.searchable : false,
    hiddenInSearchResults: schemaField ? schemaField.hiddenInSearchResults : false,
    isNullable: schemaField ? schemaField.isNullable : true,
    sortOrder: schemaField ? schemaField.sortOrder : maxParentSortOrder + 1,
    enumOptions: schemaField?.enumOptions,
    childFields: schema.schemaFields.filter((field) => isChildField(field) && field.parentId === schemaField?.id),
  };

  const { mutateAsync: updateSchema } = useUpdateEntitySchema();
  const isEditing = !!schemaField;
  const { data: personsExist } = useCheckIfPersonsExist();
  const { data: transactionsExist } = useCheckIfTransactionsExist();
  const entitiesExist =
    !!(schema.entityType === InternalApiEntityType.PERSON && personsExist) ||
    (schema.entityType === InternalApiEntityType.TRANSACTION && transactionsExist);

  const isFieldEditingRestricted = !!(
    isFeatureEnabled(ORGANISATION_FEATURE.RESTRICT_SCHEMA_FIELD_CHANGES_FOR_SEARCH_FIELDS, features) &&
    (schemaField?.searchable || schemaField?.features?.length) &&
    entitiesExist &&
    isEditing
  );

  const { notify: notifyIndexAndSchemaAreOutOfSync } = useIndexAndSchemaAreOutOfSyncNotification();

  const checkIsFieldInAttributeMappings = (fieldName: string) => {
    const attributeMappingsFieldNames = Object.values(attributeMappings.map).map(
      (attributeMapping) => attributeMapping.mapping
    );
    return attributeMappingsFieldNames.includes(fieldName);
  };

  const submit = async (values: FormikValues, { setSubmitting }: FormikHelpers<FormikValues>) => {
    const isFieldNameInAttributeMappings = checkIsFieldInAttributeMappings(values.fieldName);
    const wasSearchabilityEnabled = values.searchable && !schemaField?.searchable;

    if (isFieldNameInAttributeMappings && !wasSearchabilityEnabled) {
      values.searchable = true;
      await dispatch(
        showConfirmationModal({
          title: `The Schema Field name '${values.fieldName}' is part of the Attribute Mapping`,
          description:
            'This Schema Field has automatically become searchable. ' +
            'Searchable fields have limited editing capabilities.',
          onConfirm: null,
          cancelText: 'Close',
        })
      );
    } else if (wasSearchabilityEnabled) {
      const confirmed = await dispatch(
        showConfirmationModal({
          title: 'Are you sure you want to make this field searchable?',
          description: 'Searchable fields have limited editing capabilities',
        })
      );

      if (!confirmed) {
        setSubmitting(false);
        return;
      }
    }

    setBannerMessages('');
    try {
      setSubmitting(true);
      const { childFields: newChildFields, ...newSchemaField } = values;

      const newSchemaFields = [...schema.schemaFields];
      upsertSchemaField(newSchemaField, newSchemaFields);
      newChildFields.forEach((newChildField) => {
        if (newChildField.id === '') {
          newChildField.id = uuidv4();
        }
        upsertSchemaField(newChildField, newSchemaFields);
      });
      removeDeletedChildSchemaFields(newSchemaFields, newChildFields, newSchemaField.id);

      await updateSchema({
        entityType: schema.entityType,
        schemaFields: newSchemaFields,
      });

      await notifyIfIndexAndSchemaAreOutOfSync(newSchemaFields);

      onClose();
    } catch (e) {
      const res = e as Response;
      if (res.status === 400) {
        const { message } = (await res.json()) as ErrorResponse;
        setBannerMessages(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const upsertSchemaField = (
    newSchemaField: Omit<FormikValues, 'childFields'>,
    newSchemaFields: InternalApiEntitySchemaField[]
  ) => {
    const parentSchemaField = isChildField(newSchemaField)
      ? getParentSchemaField(newSchemaField, newSchemaFields)
      : undefined;
    const fieldIndex = newSchemaFields.findIndex((field) => field.id === newSchemaField.id);

    if (fieldIndex !== -1) {
      newSchemaFields.splice(fieldIndex, 1);
    }

    newSchemaFields.push({
      ...newSchemaField,
      ...(isChildField(newSchemaField) && {
        searchable: !!parentSchemaField?.searchable,
        hiddenInSearchResults: !!parentSchemaField?.hiddenInSearchResults,
      }),
    });
  };

  const removeDeletedChildSchemaFields = (
    newSchemaFields: InternalApiEntitySchemaField[],
    newChildFields: InternalApiEntitySchemaField[],
    parentId: string
  ) => {
    const oldChildFields = schema.schemaFields.filter((field) => field.parentId === parentId);
    oldChildFields.forEach((oldChildField) => {
      if (!newChildFields.map((newChildField) => newChildField.id).includes(oldChildField.id)) {
        newSchemaFields.splice(newSchemaFields.indexOf(oldChildField), 1);
      }
    });
  };

  const notifyIfIndexAndSchemaAreOutOfSync = async (newSchemaFields: InternalApiEntitySchemaField[]) => {
    const searchableFieldsByModification = getSearchableFieldsByModification(schema.schemaFields, newSchemaFields);
    if (
      entitiesExist &&
      (searchableFieldsByModification.fieldsWithUpdatedImportantProperties.length ||
        searchableFieldsByModification.deletedFields.length)
    ) {
      await notifyIndexAndSchemaAreOutOfSync();
    }
  };

  const RestrictedSchemaFieldExplanation = () => {
    if (!schemaField) {
      return '';
    }

    const reasons: ReactNode[] = [];
    const scenarioHandles = schemaField.features?.find(
      (feature) => feature.type === InternalApiEntitySchemaFieldFeatureTypeEnum.SCENARIOS
    )?.entities;
    if (scenarioHandles?.length) {
      reasons.push(
        <>
          Field is used in active Scenarios:{' '}
          {scenarioHandles.map((handle, index) => (
            <Fragment key={handle}>
              <InternalLink title={handle} link={getScenarioPath(handle)} />
              {index === scenarioHandles.length - 1 ? '.' : ', '}
            </Fragment>
          ))}
        </>
      );
    }
    if (schemaField.searchable) {
      reasons.push('Field is Searchable');
    }

    return (
      <div>
        <div className={'m-b-4'}>Schema Field has limited editing capabilities because of the following reasons:</div>
        <ul>
          {reasons.map((reason, i) => (
            <li key={i}>• {reason}</li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <Formik initialValues={initialValues} validateOnMount onSubmit={submit} validationSchema={validationSchema}>
      {({ values, isSubmitting, setFieldValue, setFieldTouched, isValid, errors }: FormikProps<FormikValues>) => {
        useEffect(() => {
          (async () => {
            if (values.type === InternalApiEntitySchemaFieldType.ENUM) {
              if (!initialValues.enumOptions) {
                await setFieldValue('enumOptions', ['', '']);
              }
            } else {
              await setFieldValue('enumOptions', undefined);
            }

            if (values.type === InternalApiEntitySchemaFieldType.OBJECT) {
              setModalClass('object-field');
            } else {
              setModalClass('non-object-field');
              await setFieldValue('childFields', []);
            }
          })();
        }, [values.type]);

        return (
          <Form>
            <SalvModal.Header>{schemaField ? 'Edit' : 'Add new'} field</SalvModal.Header>
            <SalvModal.Body>
              {bannerMessages &&
                bannerMessages
                  .split('\n')
                  .map((message, index) => (
                    <SalvBanner
                      key={index}
                      accent={SalvBannerAccent.STRONG}
                      message={message}
                      type={SalvBannerType.DANGER}
                      className={'m-b-8'}
                    />
                  ))}
              <div className={'m-b-24'} style={{ display: 'flex', gap: '24px' }}>
                <div style={{ flex: '0 1 50%' }}>
                  <SalvTextField
                    label='Display name'
                    description='Descriptive name for a field, for example, First name, Date of birth, etc.'
                    value={values.displayName}
                    onChange={(value: string) => setFieldValue('displayName', value)}
                  />
                </div>
                <div style={{ flex: '0 1 50%' }}>
                  <SalvTextField
                    label='Field name'
                    description='Name of the attribute, for example, first_name, dob, etc. Field name can only contain letters, numbers or underscores.'
                    value={values.fieldName}
                    disabled={isFieldEditingRestricted}
                    onChange={async (value: string) => {
                      await setFieldValue('fieldName', value);
                      await setFieldTouched('fieldName');
                    }}
                  />
                  {errors.fieldName && <div className={'salv-error-message'}>{errors.fieldName}</div>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '24px' }}>
                <div style={{ flex: '0 1 50%' }}>
                  <SalvDropdown
                    name='type'
                    label='Input type'
                    options={inputTypeOptions.filter(
                      (inputTypeOption) =>
                        schema.entityType !== InternalApiEntityType.TRANSACTION ||
                        (schema.entityType === InternalApiEntityType.TRANSACTION &&
                          inputTypeOption.key !== InternalApiEntitySchemaFieldType.OBJECT)
                    )}
                    value={inputTypeOptions.find((option) => option.key === values.type)}
                    disabled={isFieldEditingRestricted}
                    clearable={false}
                    onChange={([option]) => {
                      setFieldValue('type', option.value);
                    }}
                  />
                  {schema.entityType !== InternalApiEntityType.TRANSACTION && (
                    <SalvCheckbox
                      label={'Array'}
                      description={`Array option allows adding several values for the same data field, 
                        which are then saved as an array, for example, adding several bank account numbers.`}
                      selected={values.isMulti}
                      onChange={(isSelected) => setFieldValue('isMulti', isSelected)}
                    />
                  )}
                </div>
                <div style={{ flex: '0 1 50%' }}>
                  <SalvCheckboxGroup
                    className={'m-b-24'}
                    label={'Field options'}
                    value={{
                      isRequired: !values.isNullable,
                      isSearchable: values.searchable,
                      isVisible: !values.hidden,
                      isVisibleInSearchResults: !values.hiddenInSearchResults,
                    }}
                    onChange={async (checkBoxValues) => {
                      const isNullable = !checkBoxValues.includes('isRequired');
                      const isSearchable = checkBoxValues.includes('isSearchable');
                      const isHidden = !checkBoxValues.includes('isVisible');
                      const isHiddenInSearchResults = !checkBoxValues.includes('isVisibleInSearchResults');

                      if (values.hidden !== isHidden) {
                        await setFieldValue('hiddenInSearchResults', isHidden);
                        await setFieldValue(
                          'childFields',
                          values.childFields.map((childField) => ({ ...childField, hidden: isHidden }))
                        );
                      } else if (values.searchable !== isSearchable) {
                        await setFieldValue('hiddenInSearchResults', isSearchable ? isHidden : true);
                      } else {
                        await setFieldValue('hiddenInSearchResults', isHiddenInSearchResults);
                      }

                      await setFieldValue('isNullable', isNullable);
                      await setFieldValue('searchable', isSearchable);
                      await setFieldValue('hidden', isHidden);
                    }}
                  >
                    <SalvGroupedCheckbox label={'Required'} value={'isRequired'} />
                    <SalvGroupedCheckbox
                      label={'Searchable'}
                      value={'isSearchable'}
                      disabled={
                        isFeatureEnabled(
                          ORGANISATION_FEATURE.RESTRICT_SCHEMA_FIELD_CHANGES_FOR_SEARCH_FIELDS,
                          features
                        ) && schemaField?.searchable
                      }
                    />
                    <SalvGroupedCheckbox label={'Visible'} value={'isVisible'} />
                    {values.searchable && (
                      <div className={'m-l-16'}>
                        <SalvGroupedCheckbox
                          label={'Visible in search results'}
                          value={'isVisibleInSearchResults'}
                          disabled={values.hidden}
                        />
                      </div>
                    )}
                  </SalvCheckboxGroup>
                </div>
              </div>
              {values.type === InternalApiEntitySchemaFieldType.OBJECT && (
                <SchemaChildFields
                  isFieldEditingRestricted={isFieldEditingRestricted}
                  values={values}
                  setFieldValue={setFieldValue}
                  errors={errors}
                />
              )}
              {values.type === InternalApiEntitySchemaFieldType.ENUM && values.enumOptions && (
                <SchemaEnumOptions
                  className={'m-t-24'}
                  enumOptions={values.enumOptions}
                  setEnumOptions={async (values) => {
                    await setFieldValue('enumOptions', values);
                  }}
                  size={Size.SMALL}
                  errors={errors}
                />
              )}
            </SalvModal.Body>
            <SalvModal.Footer>
              {isFieldEditingRestricted && (
                <div
                  className='text-secondary'
                  style={{ width: '100%', display: 'flex', gap: '4px', alignItems: 'center' }}
                >
                  <span>Why are some form fields disabled?</span>
                  <SalvTooltipWrapper tooltip={<RestrictedSchemaFieldExplanation />}>
                    <FontAwesomeIcon icon={faCircleInfo} className='cursor-pointer' />
                  </SalvTooltipWrapper>
                </div>
              )}
              <SalvButton
                label={'Close'}
                variant='secondary'
                onClick={() => {
                  onClose();
                }}
              />
              <SalvButton label={'Save'} disabled={isSubmitting || !isValid} type='submit' variant='primary' />
            </SalvModal.Footer>
          </Form>
        );
      }}
    </Formik>
  );
};

export default EntitySchemaField;
